import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PackageCheck, ClipboardCheck, CheckCircle2, AlertTriangle, FileText, RefreshCw, Search, Lock, History, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge } from '../components/guias/GuiaViewer';
import { ConferenciaEntrega } from '../components/guias/ConferenciaEntrega';
import { IndicadoresGuias } from '../components/guias/IndicadoresGuias';
import { STATUS_STYLE, errMsg, fmtDate, fmtDateTime } from '../components/guias/guiasUtils';

const KPI = ({ icon: Icon, label, value, cls = '' }) => (
  <Card><CardContent className="p-3 flex items-center gap-3">
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${cls || 'bg-primary/10 text-primary'}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-xl font-bold" data-testid={`rec-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value ?? '-'}</p></div>
  </CardContent></Card>
);

export default function RecebimentoGuias() {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('entregas');
  const [fCat, setFCat] = useState('todas');
  const [fStatus, setFStatus] = useState('pendentes');
  const [fBusca, setFBusca] = useState('');
  const [hBusca, setHBusca] = useState('');
  const [hPaciente, setHPaciente] = useState('');
  const [hIni, setHIni] = useState('');
  const [hFim, setHFim] = useState('');
  const [hDiv, setHDiv] = useState(false);
  const [competencia, setCompetencia] = useState(params.get('competencia') || '');
  const selected = params.get('entrega');

  useEffect(() => {
    api.get('/guias/meta').then((r) => { setMeta(r.data); if (!params.get('competencia') && competencia === '') setCompetencia(r.data.hoje); });
  }, [api]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (fCat !== 'todas') q.categoria = fCat;
      if (fStatus !== 'pendentes') q.status = fStatus;
      if (fBusca) q.busca = fBusca;
      if (competencia) q.competencia = competencia;
      const hq = { busca: hBusca || undefined, paciente: hPaciente || undefined, data_inicio: hIni || (competencia || undefined), data_fim: hFim || (competencia || undefined), com_divergencia: hDiv || undefined };
      const [d, l, h] = await Promise.all([
        api.get('/guias/recebimento/dashboard'), api.get('/guias/recebimento/entregas', { params: q }), api.get('/guias/recebimento/historico', { params: hq }),
      ]);
      setDash(d.data); setEntregas(l.data.entregas); setHistorico(h.data.entregas);
    } catch (e) { toast.error(errMsg(e, 'Erro ao carregar recebimentos')); }
    finally { setLoading(false); }
  }, [api, fCat, fStatus, fBusca, hBusca, hPaciente, hIni, hFim, hDiv, competencia]);

  useEffect(() => { load(); }, [load]);

  const open = (id) => setParams(competencia ? { entrega: id, competencia } : { entrega: id });
  const close = () => setParams(competencia ? { competencia } : {});

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-5 max-w-7xl mx-auto" data-testid="recebimento-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><PackageCheck className="h-7 w-7 text-primary" />Recebimento e Conferência de Guias</h1>
            <p className="text-sm text-muted-foreground">Segunda camada de controle: conferência física e comprovação das guias entregues.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Competência</label>
            <Input type="date" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-[160px]" data-testid="rec-input-competencia" />
            {competencia && <Button variant="ghost" size="sm" onClick={() => setCompetencia('')} data-testid="btn-rec-todas-competencias">Todas</Button>}
            <Button variant="outline" size="icon" onClick={load} data-testid="btn-rec-atualizar"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>

        {selected ? (
          <ConferenciaEntrega entregaId={selected} onBack={close} onChanged={load} />
        ) : (
          <>
            {dash && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KPI icon={PackageCheck} label="Aguardando recebimento" value={dash.aguardando_recebimento} cls="bg-yellow-100 text-yellow-700" />
                <KPI icon={ClipboardCheck} label="Em conferência" value={dash.em_conferencia} cls="bg-blue-100 text-blue-700" />
                <KPI icon={CheckCircle2} label="Recebidas hoje" value={dash.recebidas_hoje} cls="bg-emerald-100 text-emerald-700" />
                <KPI icon={AlertTriangle} label="Com divergência" value={dash.com_divergencia} cls="bg-orange-100 text-orange-700" />
                <KPI icon={FileText} label="Guias recebidas hoje" value={dash.guias_recebidas_hoje} />
                <KPI icon={FileText} label="Guias pendentes" value={dash.guias_pendentes_conferencia} cls="bg-red-100 text-red-700" />
              </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="entregas" data-testid="tab-entregas">Entregas ({entregas.length})</TabsTrigger>
                <TabsTrigger value="historico" data-testid="tab-historico"><History className="h-4 w-4 mr-1" />Histórico de Recebimentos</TabsTrigger>
                <TabsTrigger value="indicadores" data-testid="tab-indicadores"><BarChart3 className="h-4 w-4 mr-1" />Indicadores</TabsTrigger>
              </TabsList>
              <TabsContent value="indicadores" className="mt-3">{tab === 'indicadores' && <IndicadoresGuias meta={meta} competencia={competencia} />}</TabsContent>

              <TabsContent value="entregas" className="space-y-3 mt-3">
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" /><Input placeholder="Buscar profissional..." value={fBusca} onChange={(e) => setFBusca(e.target.value)} className="pl-8" data-testid="rec-input-busca" /></div>
                  <Select value={fCat} onValueChange={setFCat}><SelectTrigger className="w-[190px]" data-testid="rec-select-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="todas">Todas as categorias</SelectItem>{meta && Object.entries(meta.categorias).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                  <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-[220px]" data-testid="rec-select-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pendentes">Pendentes de recebimento</SelectItem>{['aguardando_conferencia', 'em_conferencia', 'com_divergencia', 'recebido'].map((k) => <SelectItem key={k} value={k}>{STATUS_STYLE[k].label}</SelectItem>)}</SelectContent></Select>
                </div>
                <Card><CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Profissional</TableHead><TableHead>Categoria</TableHead><TableHead>Competência</TableHead><TableHead className="text-center">Pacientes</TableHead><TableHead className="text-center">Guias</TableHead><TableHead className="text-center">Conferidas</TableHead><TableHead className="text-center">Pendências</TableHead><TableHead>Status</TableHead><TableHead>Ação</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {entregas.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Nenhuma entrega pendente de recebimento.</TableCell></TableRow>}
                      {entregas.map((e) => {
                        const pend = e.pendentes + e.inconsistentes;
                        return (
                          <TableRow key={e.id} data-testid={`entrega-row-${e.id}`}>
                            <TableCell className="font-medium">{e.profissional_nome}<p className="text-xs text-muted-foreground">Entregue por {e.entregue_por_nome} · {fmtDateTime(e.entregue_em)}</p></TableCell>
                            <TableCell>{e.categoria_label}</TableCell>
                            <TableCell>{fmtDate(e.competencia)}</TableCell>
                            <TableCell className="text-center">{e.esperadas}</TableCell>
                            <TableCell className="text-center">{e.lancadas}</TableCell>
                            <TableCell className="text-center">{e.conferidas}</TableCell>
                            <TableCell className={`text-center font-bold ${pend > 0 ? 'text-red-600' : 'text-green-600'}`}>{pend}</TableCell>
                            <TableCell><StatusBadge status={e.status} /></TableCell>
                            <TableCell><Button size="sm" onClick={() => open(e.id)} variant={pend > 0 ? 'outline' : 'default'} data-testid={`btn-conferir-${e.id}`}>{pend > 0 ? <Lock className="h-4 w-4 mr-1" /> : <ClipboardCheck className="h-4 w-4 mr-1" />}{e.status === 'recebido' ? 'Ver' : 'Conferir'}</Button></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent></Card>
              </TabsContent>

              <TabsContent value="historico" className="space-y-3 mt-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <Input placeholder="Profissional, Nº recebimento ou recebedor" value={hBusca} onChange={(e) => setHBusca(e.target.value)} className="w-[260px]" data-testid="hist-input-busca" />
                  <Input placeholder="Paciente" value={hPaciente} onChange={(e) => setHPaciente(e.target.value)} className="w-[180px]" data-testid="hist-input-paciente" />
                  <Input type="date" value={hIni} onChange={(e) => setHIni(e.target.value)} className="w-[150px]" data-testid="hist-input-ini" />
                  <Input type="date" value={hFim} onChange={(e) => setHFim(e.target.value)} className="w-[150px]" data-testid="hist-input-fim" />
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={hDiv} onChange={(e) => setHDiv(e.target.checked)} data-testid="hist-chk-div" />Com divergência</label>
                </div>
                <Card><CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Nº</TableHead><TableHead>Profissional</TableHead><TableHead>Categoria</TableHead><TableHead>Competência</TableHead><TableHead className="text-center">Pacientes</TableHead><TableHead className="text-center">Guias</TableHead><TableHead>Recebedor</TableHead><TableHead>Data</TableHead><TableHead className="text-center">Diverg.</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                    <TableBody>
                      {historico.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Nenhum recebimento encontrado.</TableCell></TableRow>}
                      {historico.map((e) => (
                        <TableRow key={e.id} data-testid={`hist-row-${e.id}`}>
                          <TableCell className="font-mono text-xs">{e.numero_recebimento || '-'}</TableCell>
                          <TableCell className="font-medium">{e.profissional_nome}</TableCell>
                          <TableCell>{e.categoria_label}</TableCell>
                          <TableCell>{fmtDate(e.competencia)}</TableCell>
                          <TableCell className="text-center">{e.total_pacientes_recebidos ?? e.total_pacientes_entrega ?? '-'}</TableCell>
                          <TableCell className="text-center">{e.total_guias_recebidas ?? e.total_guias_entrega ?? '-'}</TableCell>
                          <TableCell>{e.recebido_por_nome || '-'}</TableCell>
                          <TableCell>{e.recebido_em ? fmtDateTime(e.recebido_em) : '-'}</TableCell>
                          <TableCell className={`text-center ${e.total_divergencias > 0 ? 'text-orange-600 font-bold' : ''}`}>{e.total_divergencias}</TableCell>
                          <TableCell><StatusBadge status={e.status} /></TableCell>
                          <TableCell><Button size="sm" variant="outline" onClick={() => open(e.id)} data-testid={`btn-hist-ver-${e.id}`}>Ver</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent></Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
}
